import { useLingui } from "@lingui/react/macro";
import styles from "./ShiftCard.module.scss";
import { Text, TextWithEllipsis } from "@medoma/components/Text/Text";
import { employeeName } from "@medoma/models/shifts";
import { formattedTimeSpan } from "@/components/Time/timeUtils";
import { CategoryIcon } from "@/components/CategoryIcon/CategoryIcon";
import {
  categorySchema,
  getGroupStatusTimestamp,
  isGroup,
  timeOfDayDictionary,
  timeOfDaySchema,
  visitStatusSchema,
  type IActivityCategory,
  type IActivityOccurrenceStatus,
  type ITimeOfDay,
  type IGroupStatus,
} from "@medoma/models/activities";
import { calculateVisitTimes, type IVisitStatus } from "@medoma/models/visits";
import { getPatientNameWithStatus } from "@/api/Patients";
import type { listPatientSchema } from "@medoma/models/patients";
import type { z } from "zod";
import { Heading } from "@medoma/components/Heading/Heading";
import { ShiftTime } from "@/components/Time/ShiftTime";
import { startOfToday } from "date-fns";
import { StatusTag } from "@/components/StatusTag/StatusTag";
import { UnstyledLink } from "@medoma/components/Link/Link";
import type { IShiftWithContents } from "@medoma/models/shiftWithContents";

type IShiftCard = {
  shiftWithContents: IShiftWithContents;
};

type IListPatient = z.infer<typeof listPatientSchema>;

type ActivityOrVisitItem = {
  id: string;
  category: IActivityCategory;
  patient: IListPatient | null | undefined;
  title: string;
  finishedAt: Date | null | undefined;
} & (
  | {
      type: "visit";
      status: IVisitStatus | IGroupStatus | null | undefined;
      start: Date | undefined;
      end: Date | undefined;
      timeOfDay: ITimeOfDay | undefined;
      startedTravellingToAt?: Date | null;
      startedAt?: Date | null;
    }
  | {
      type: "activity";
      status: IActivityOccurrenceStatus;
      start: Date;
      end: Date;
      timeOfDay: ITimeOfDay;
    }
);

type ShiftItem = IShiftWithContents["items"][number];
type ShiftVisit = Extract<ShiftItem, { visits: unknown }>["visits"][number];
type ShiftGroup = Extract<ShiftItem, { occurrences: unknown }>;
type ShiftActivity = Extract<ShiftItem, { activityId: unknown }>;

const toVisitItem = (visit: ShiftVisit): ActivityOrVisitItem => {
  // Might want to rewrite calculateVisitTimes, or do a local custom visit
  // times calculation here, to better handle JIT visits where start and end
  // are the same.
  const { start, end, isAnyTimeOfDay } = calculateVisitTimes(visit);

  return {
    type: "visit",
    id: visit.id,
    category: categorySchema.enum.HomeVisit,
    patient: visit.patient,
    title: visit.occurrences[0]?.title || "",
    start,
    end,
    timeOfDay: isAnyTimeOfDay
      ? timeOfDaySchema.enum.Any
      : timeOfDaySchema.enum.Specific,
    status: visit.status,
    startedTravellingToAt: visit.startedTravellingToAt,
    startedAt: visit.startedAt,
    finishedAt: visit.finishedAt,
  };
};

const toGroupItem = (group: ShiftGroup): ActivityOrVisitItem => ({
  type: "visit",
  id: group.id,
  category: group.category,
  patient: group.patient,
  title: group.occurrences[0]?.title || "",
  start: group.start,
  end: group.end,
  timeOfDay: group.timeOfDay,
  status: group.visitStatus,
  finishedAt: getGroupStatusTimestamp(group),
});

const toActivityItem = (activity: ShiftActivity): ActivityOrVisitItem => ({
  type: "activity",
  id: activity.id,
  category: activity.category,
  patient: "patient" in activity ? (activity.patient ?? null) : null,
  title: activity.title,
  start: activity.start,
  end: activity.end,
  timeOfDay: activity.timeOfDay,
  status: activity.status,
  finishedAt: activity.finishedAt,
});

const toActivityOrVisitItems = (item: ShiftItem): ActivityOrVisitItem[] => {
  if ("visits" in item) {
    return item.visits.map(toVisitItem);
  }

  if (isGroup(item)) {
    return [toGroupItem(item)];
  }

  if ("activityId" in item) {
    return [toActivityItem(item)];
  }

  throw item satisfies never;
};

const sortActivitiesAndVisits = (items: ActivityOrVisitItem[]) => {
  return [...items].sort((a, b) => {
    // Any time of day items go to the end
    const aIsAny = a.timeOfDay === timeOfDaySchema.enum.Any;
    const bIsAny = b.timeOfDay === timeOfDaySchema.enum.Any;
    if (aIsAny !== bIsAny) {
      return aIsAny ? 1 : -1;
    }

    // Visits come before activities
    const aIsVisit = a.type === "visit";
    const bIsVisit = b.type === "visit";
    if (aIsVisit !== bIsVisit) {
      return aIsVisit ? -1 : 1;
    }

    // If one of the items has no start time, sort it to the end
    if (!a.start || !b.start) {
      return a.start ? -1 : 1;
    }

    // Sort by start time
    const startDiff = a.start.getTime() - b.start.getTime();
    if (startDiff !== 0) {
      return startDiff;
    }

    // If one of the items has no end time, sort it to the end
    if (!a.end || !b.end) {
      return a.end ? -1 : 1;
    }

    // Sort by end time
    return a.end.getTime() - b.end.getTime();
  });
};

const StatusOrTimestamp = ({ item }: { item: ActivityOrVisitItem }) => {
  const { t } = useLingui();
  const displayTime =
    item.timeOfDay === timeOfDaySchema.enum.Any
      ? t(timeOfDayDictionary.Any.short)
      : item.start && item.end
        ? formattedTimeSpan(item.start, item.end)
        : "–"; // Fallback for when calculateVisitTimes returns undefined (idea to refactor this in the future)

  if (item.type === "visit") {
    switch (item.status) {
      case visitStatusSchema.enum.travellingTo:
        return (
          <StatusTag
            status={visitStatusSchema.enum.travellingTo}
            timestamp={item.startedTravellingToAt}
            variant={
              item.startedTravellingToAt ? "icon-with-timestamp" : "icon"
            }
          />
        );
      case visitStatusSchema.enum.ongoing:
        return (
          <StatusTag
            status={visitStatusSchema.enum.ongoing}
            timestamp={item.startedAt}
            variant={item.startedAt ? "icon-with-timestamp" : "icon"}
          />
        );
      case visitStatusSchema.enum.finished:
        return (
          <StatusTag
            status={visitStatusSchema.enum.finished}
            timestamp={item.finishedAt}
            variant={item.finishedAt ? "icon-with-timestamp" : "icon"}
          />
        );
      default:
        return (
          <Text element="div" color="faded">
            {displayTime}
          </Text>
        );
    }
  }

  if (item.type === "activity" && item.finishedAt) {
    return (
      <StatusTag
        status="finished"
        timestamp={item.finishedAt}
        variant="icon-with-timestamp"
      />
    );
  }

  if (!displayTime) {
    return null;
  }

  return (
    <Text element="div" color="faded">
      {displayTime}
    </Text>
  );
};

export const ShiftCard = ({ shiftWithContents }: IShiftCard) => {
  const { t } = useLingui();
  const { shift, items } = shiftWithContents;
  const today = startOfToday();

  const activitiesAndVisits = sortActivitiesAndVisits(
    items.flatMap(toActivityOrVisitItems),
  );

  const employeeDisplayName = shift.employee
    ? employeeName(shift.employee, { employeeNameFormat: "full" })
    : t`Obemannad`;

  return (
    <article className={styles.shiftCard}>
      <header className={styles.header}>
        <Heading level="h2" size="h3">
          {employeeDisplayName}
        </Heading>
        <ShiftTime
          relativeToDate={today}
          startDateTime={shift.startDateTime}
          endDateTime={shift.endDateTime}
        />
      </header>

      {activitiesAndVisits.length > 0 ? (
        <ul className={styles.itemsList}>
          {activitiesAndVisits.map((item) => {
            const displayTitle = item.patient
              ? getPatientNameWithStatus(item.patient, "full")
              : item.title;
            const content = (
              <div className={styles.activityItem}>
                <CategoryIcon category={item.category} size="small" />
                <div className={styles.activityContent}>
                  <TextWithEllipsis
                    element="div"
                    maxWidth="100%"
                    title={displayTitle}
                  >
                    {displayTitle}
                  </TextWithEllipsis>
                  <StatusOrTimestamp item={item} />
                </div>
              </div>
            );

            return (
              <li key={item.id}>
                {item.patient ? (
                  <UnstyledLink
                    aria-label={displayTitle}
                    to={`/patients/${item.patient.id}`}
                  >
                    {content}
                  </UnstyledLink>
                ) : (
                  content
                )}
              </li>
            );
          })}
        </ul>
      ) : null}
    </article>
  );
};
